/**
 * Fetch Cart Items
 * @class Cart
 * @description Class to handle cart operations
 */
class Cart {
  /**
   * Fetch cart items
   * @returns {Promise<Array>} - List of cart items
   */
  static async fetchCartItems() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Network response was not ok');
      const cart = await response.json();
      return cart.items;
    } catch (error) {
      console.error('Error fetching cart items:', error);
      return [];
    }
  }
}

/**
 * Fetch Recommendations
 * @class Recommendations
 * @description Class to handle product recommendations
 */
class Recommendations {
  /**
   * Fetch recommendations based on a product ID
   * @param {string} productId - Product ID to fetch recommendations for
   * @param {string} sectionId - Section ID to be used for recommendations HTML generation
   * @returns {Promise<string>} - Recommendations HTML
   */
  static async fetchRecommendations(productId, sectionId, limit = 4) {
    try {
      const sectionID = sectionId ? sectionId : 'cart-drawer-recommendations';
      const response = await fetch(`/recommendations/products?product_id=${productId}&section_id=${sectionID}&limit=${limit}&intent=complementary`);
      if (!response.ok) throw new Error('Network response was not ok');
      return await response.text();
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      return '';
    }
  }
}

/**
 * Cart Drawer
 * @class CartDrawerRecommendations
 * @description Class to handle cart drawer recommendation operations
 */
class CartDrawerRecommendations {
  /**
   * Display recommendations in the cart drawer
   */
  async displayCartRecommendations() {
    try {
      const productRecommendationsSection = document.querySelector('.cart-drawer-left .cart-drawer-recommendations-response');
      const mobileRecommendationsSection = document.querySelector('.cart-drawer-right .cart-drawer-recommendations-response');
      const cartItems = await Cart.fetchCartItems();
      let productId = productRecommendationsSection.getAttribute('data-fallback-product');
      if (cartItems.length > 0) {
        const mostExpensiveProduct = cartItems.reduce((prev, current) => (prev.final_price > current.final_price) ? prev : current);
        if (mostExpensiveProduct && mostExpensiveProduct.vendor != 'Extend') {
          productId = mostExpensiveProduct.product_id;
        }
      }
      const recommendationsHTML = await Recommendations.fetchRecommendations(productId);
      const width = window.innerWidth;
      if (width <= 768) {
        const dropdownIcon = document.querySelector('.cart-title-clickable');
        const dropdownList = document.querySelector('.recommendation-dropdown');
        if (dropdownIcon.dataset.opened === 'false') {
          dropdownList.classList.add('hidden');
        } else{
          dropdownList.classList.remove('hidden');
        }
        productRecommendationsSection.innerHTML = '';
        mobileRecommendationsSection.innerHTML = recommendationsHTML.trim().length ? recommendationsHTML : '';
      } else {
        mobileRecommendationsSection.innerHTML = '';
        productRecommendationsSection.innerHTML = recommendationsHTML.trim().length ? recommendationsHTML : '';
      }
    } catch (error) {
      console.error('Error displaying recommendations:', error);
    }
  }

  async displayNotificationDrawerRecommendations(parent) {
    try {
      const productRecommendationsSection = parent.querySelector('.variant-added-recommendations-response');
      const cartItems = await Cart.fetchCartItems();
      let productId = productRecommendationsSection.getAttribute('data-fallback-product');
      if (cartItems.length > 0) {
        const mostExpensiveProduct = cartItems.reduce((prev, current) => (prev.final_price > current.final_price) ? prev : current);
        if (mostExpensiveProduct && mostExpensiveProduct.vendor != 'Extend') {
          productId = mostExpensiveProduct.product_id;
        }
      }
      const recommendationsHTML = await Recommendations.fetchRecommendations(productId, 'notification-drawer-recommendations', 3);
      
      productRecommendationsSection.innerHTML = recommendationsHTML.trim().length ? recommendationsHTML : '';

      if (recommendationsHTML.trim().length) {
        this.fireNotificationDrawerViewItemList(productRecommendationsSection);
      }

      const buttons = parent.querySelectorAll('.small-product-card-atc button[aria-controls]');

      buttons.forEach((button) => {
        button.addEventListener('click', (e) => {
          let drawer = e.target.closest('cart-notification-drawer')
          if (!drawer) {
            drawer = document.querySelector('quick-buy-drawer');
          }
          if (drawer && drawer.shadowRoot) {
            const closeButton = drawer.shadowRoot.querySelector('button[part=outside-close-button')
            if (closeButton) {
              closeButton.click();
            } 
          }
        });
      });
      
    } catch (error) {
      console.error('Error displaying recommendations:', error);
    }
  }

  /**
   * Fires a `dl_view_item_list` event (matching the Elevar collection-page
   * syntax) for the "Others Also Liked" recommendations shown in the post-ATC
   * popup. Fires once per popup impression — this method runs each time the
   * recommendations are rendered into the drawer.
   * @param {HTMLElement} container - Element the recommendations were injected into
   */
  fireNotificationDrawerViewItemList(container) {
    try {
      const dataEl = container.querySelector('.notification-drawer-recommendations-json');
      if (!dataEl) {
        return;
      }

      let products;
      try {
        products = (JSON.parse(dataEl.textContent) || {}).impressions || [];
      } catch (error) {
        console.warn('Notification drawer view_item_list: failed to parse products JSON', error);
        return;
      }

      if (!products.length) {
        return;
      }

      const list = window.location.pathname;
      const toPrice = (cents) => {
        const number = parseInt(cents, 10);
        return isNaN(number) ? '0.00' : (number / 100).toFixed(2);
      };

      const impressions = products.map((product, index) => ({
        id: product.id || product.variant_id || '',
        name: product.name || '',
        brand: product.brand || 'Casper',
        category: product.category || '',
        variant: product.variant || '',
        price: toPrice(product.price_cents),
        position: product.position || index + 1,
        list: list,
        product_id: product.product_id || '',
        variant_id: product.variant_id || '',
        compare_at_price: toPrice(product.compare_at_price_cents),
        image: (product.image || '').trim()
      }));

      const currencyCode = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';

      window.ElevarDataLayer = window.ElevarDataLayer || [];
      window.ElevarDataLayer.push({
        event: 'dl_view_item_list',
        view_location: 'pdp_post_atc_recommendation',
        ecommerce: {
          currencyCode: currencyCode,
          impressions: impressions
        }
      });
    } catch (error) {
      console.warn('Error firing notification drawer view_item_list:', error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new CartDrawerRecommendations());

export {
  Cart,
  Recommendations,
  CartDrawerRecommendations
}
